import React, { useState } from 'react';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { EventCreationForm } from './EventCreationForm';
import { LineupQuickActions } from './LineupQuickActions';
import { ConfirmModal } from './ConfirmModal';
import { SubstitutionModal } from './SubstitutionModal';
import { CaptainTransferModal } from './CaptainTransferModal';
import { ScoreDisplay } from './ScoreDisplay';
import { MatchTimeControls } from './MatchTimeControls';
import { MatchStatusControl } from './MatchStatusControl';
import { VARReviewModal } from './VARReviewModal';
import { OperatorModeSelector } from './OperatorModeSelector';
import { AutoHideHeader } from './AutoHideHeader';

const tabs = [
  { key: 'match', label: 'Match', icon: '⚽' },
  { key: 'clock', label: 'Clock', icon: '⏱️' },
  { key: 'home', label: 'Home', icon: '🏠' },
  { key: 'away', label: 'Away', icon: '✈️' },
  { key: 'events', label: 'Events', icon: '📋' }
];

export const LiveMatchViewTouch = ({ matchId }) => {
  const m = useLiveMatch(matchId);
  const [activeTab, setActiveTab] = useState('match');

  if (!m.connected) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🔄</div>
          <h2 className="text-2xl font-bold mb-2">Connecting...</h2>
          <p className="text-gray-500">Establishing WebSocket connection</p>
        </div>
      </div>
    );
  }

  const halfLabel = m.clock.half === 1 ? '1st' : m.clock.half === 2 ? '2nd' : m.clock.half === 3 ? 'ET1' : 'ET2';

  return (
    <>
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Auto-hide Header (touch-optimized) */}
      <AutoHideHeader timeout={5000}>
        {/* Persistent Header */}
        <div className="bg-white shadow-md px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Connection indicator */}
            <div className={`w-3 h-3 rounded-full ${m.connected ? 'bg-green-500' : 'bg-red-500'}`} />
            {/* Half */}
            <span className="text-sm font-bold text-gray-500 uppercase">{halfLabel}</span>
            {/* Clock */}
            <span className="text-2xl font-mono font-bold text-gray-800">
              {String(m.clock.minute).padStart(2, '0')}:{String(m.clock.second).padStart(2, '0')}
            </span>
            {m.clock.injuryTime > 0 && (
              <span className="text-lg font-mono text-red-600 font-bold">+{m.clock.injuryTime}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Compact score */}
            <div className="text-2xl font-bold">
              <span className="text-blue-700">{m.score.home}</span>
              <span className="text-gray-400 mx-1">-</span>
              <span className="text-green-700">{m.score.away}</span>
            </div>
            <OperatorModeSelector currentMode="touch" matchId={matchId} />
          </div>
        </div>

        {/* Captain Alert */}
        {m.captainAlert && (
          <div className="bg-red-50 border-b-2 border-red-300 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              <div>
                <span className="font-bold text-red-800">Captain Required!</span>
                <span className="text-red-700 ml-2 text-sm">{m.captainAlert.playerName} expelled</span>
              </div>
            </div>
            <button
              onClick={() => m.setCaptainAlert(null)}
              className="text-red-600 font-bold text-xl min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              ×
            </button>
          </div>
        )}
      </AutoHideHeader>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {activeTab === 'match' && (
          <div className="p-4 space-y-4">
            <MatchStatusControl
              match={m.match}
              lineupStatus={m.lineupStatus}
              onStartMatch={() => m.setConfirmModal({
                isOpen: true,
                action: 'start',
                title: 'Start Match',
                message: 'Start this match? The operator will control when to start the clock.'
              })}
              onEndMatch={() => {
                if (m.clock.running) {
                  alert('Please stop the clock before ending the match');
                  return;
                }
                m.setConfirmModal({
                  isOpen: true,
                  action: 'end',
                  title: 'End Match',
                  message: 'End this match? This action cannot be undone.'
                });
              }}
              onCancelMatch={() => m.setConfirmModal({
                isOpen: true,
                action: 'cancel',
                title: 'Cancel Match',
                message: 'Cancel this match? The clock will be stopped if running.'
              })}
              onNavigateToLineup={() => m.navigate(`/lineups/${matchId}`)}
              clockRunning={m.clock.running}
              captainsAssigned={m.captainsAssigned()}
            />

            <ScoreDisplay
              score={m.score}
              homeTeam={m.match?.home_team}
              awayTeam={m.match?.away_team}
              onUpdateScore={m.handleScoreUpdate}
              scoreboardVisible={m.scoreboardVisible}
              onToggleScoreboard={m.toggleScoreboard}
              scoreboardTimeout={m.scoreboardTimeout}
              onScoreboardTimeoutChange={m.handleScoreboardTimeoutChange}
              formationsVisible={m.formationsVisible}
              onToggleFormations={m.toggleFormations}
              logoVisible={m.logoVisible}
              onToggleLogo={m.toggleLogo}
              match={m.match}
              joined={m.joined}
            />

            {/* Quick view links */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={m.openPublicView}
                className="min-h-[56px] bg-purple-600 text-white rounded-xl font-medium text-lg hover:bg-purple-700 active:bg-purple-800"
              >
                📺 Public View
              </button>
              <button
                onClick={m.openOBSView}
                className="min-h-[56px] bg-indigo-600 text-white rounded-xl font-medium text-lg hover:bg-indigo-700 active:bg-indigo-800"
              >
                🎥 Vision Master
              </button>
            </div>
          </div>
        )}

        {activeTab === 'clock' && (
          <div className="p-4">
            {/* Large clock display */}
            <div className="text-center mb-6 bg-white rounded-xl p-6 shadow-sm">
              <div className="text-sm font-bold text-gray-500 uppercase mb-2">{halfLabel} Half</div>
              <div className="text-6xl font-mono font-bold text-gray-800 mb-2">
                {String(m.clock.minute).padStart(2, '0')}:{String(m.clock.second).padStart(2, '0')}
              </div>
              {m.clock.injuryTime > 0 && (
                <div className="text-2xl font-mono text-red-600 font-bold">+{m.clock.injuryTime}</div>
              )}
              <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${
                m.clock.running ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                <span className={`w-2 h-2 rounded-full ${m.clock.running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                {m.clock.running ? 'RUNNING' : 'STOPPED'}
              </div>
            </div>

            <MatchTimeControls
              clock={m.clock}
              onStartClock={m.handleStartClock}
              onStopClock={m.stopClock}
              onResetClock={m.resetClock}
              onSetTime={m.setTime}
              onSetInjuryTime={m.setInjuryTime}
              onChangeHalf={m.changeHalf}
              joined={m.joined}
            />
          </div>
        )}

        {activeTab === 'home' && (
          <div className="p-4">
            {!m.lineupLoading && (
              <LineupQuickActions
                matchId={matchId}
                homeTeam={m.homeTeam}
                awayTeam={m.awayTeam}
                activeCounts={m.activeCounts}
                onEventCreated={m.handleQuickEvent}
                teamFilter="home"
                columns={1}
              />
            )}
          </div>
        )}

        {activeTab === 'away' && (
          <div className="p-4">
            {!m.lineupLoading && (
              <LineupQuickActions
                matchId={matchId}
                homeTeam={m.homeTeam}
                awayTeam={m.awayTeam}
                activeCounts={m.activeCounts}
                onEventCreated={m.handleQuickEvent}
                teamFilter="away"
                columns={1}
              />
            )}
          </div>
        )}

        {activeTab === 'events' && (
          <div className="p-4 space-y-4">
            {/* Recent Events */}
            {m.events.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-bold text-gray-700">Recent Events ({m.events.length})</h3>
                </div>
                <div className="p-3 space-y-2 max-h-[40vh] overflow-y-auto">
                  {[...m.events].reverse().slice(0, 20).map((evt) => {
                    const evtPlayerName = evt.player
                      ? `${evt.player.first_name} ${evt.player.last_name}`
                      : 'Unknown';
                    const isVAReligible = ['goal', 'penalty_goal', 'own_goal', 'penalty', 'red_card'].some(t =>
                      evt.event_type?.toLowerCase().includes(t)
                    );
                    return (
                      <div
                        key={evt.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          evt.is_cancelled ? 'bg-red-50 border-red-200 opacity-60' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3 text-sm min-w-0">
                          <span className="font-mono font-bold text-gray-600 flex-shrink-0">
                            {evt.minute}'
                          </span>
                          <span className="font-semibold truncate">
                            {evtPlayerName}
                          </span>
                          <span className="text-gray-500 flex-shrink-0">
                            {evt.event_type?.replace(/_/g, ' ')}
                          </span>
                          {evt.is_cancelled && (
                            <span className="text-xs text-red-600 font-semibold flex-shrink-0">CANCELLED</span>
                          )}
                        </div>
                        {!evt.is_cancelled && isVAReligible && (
                          <button
                            onClick={() => m.setVarModal({ isOpen: true, event: evt })}
                            className="min-h-[44px] px-4 bg-blue-700 text-white text-xs font-bold rounded-lg hover:bg-blue-800 flex-shrink-0 ml-2"
                          >
                            VAR
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Advanced Event Creation */}
            <div className="bg-white rounded-xl shadow-sm">
              <details>
                <summary className="cursor-pointer p-4 font-bold text-gray-700">
                  Advanced Event Creation
                </summary>
                <div className="p-4 border-t border-gray-100">
                  <EventCreationForm
                    matchId={matchId}
                    clock={m.clock}
                    onEventCreated={m.createEvent}
                  />
                </div>
              </details>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-50">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex flex-col items-center py-2 min-h-[56px] transition-colors ${
              activeTab === tab.key
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 active:bg-gray-100'
            }`}
          >
            <span className="text-xl">{tab.icon}</span>
            <span className="text-[10px] font-bold uppercase mt-0.5">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>

    {/* Modals */}
    <ConfirmModal
      isOpen={m.confirmModal.isOpen}
      title={m.confirmModal.title}
      message={m.confirmModal.message}
      confirmText={m.confirmModal.action === 'start' ? 'Start Match' : m.confirmModal.action === 'end' ? 'End Match' : 'Cancel Match'}
      confirmClass={
        m.confirmModal.action === 'start' ? 'bg-green-600 hover:bg-green-700' :
        m.confirmModal.action === 'end' ? 'bg-blue-600 hover:bg-blue-700' :
        'bg-red-600 hover:bg-red-700'
      }
      onConfirm={() => {
        if (m.confirmModal.action === 'start') m.handleStartMatch();
        else if (m.confirmModal.action === 'end') m.handleEndMatch();
        else if (m.confirmModal.action === 'cancel') m.handleCancelMatch();
      }}
      onCancel={() => m.setConfirmModal({ isOpen: false, action: null, title: '', message: '' })}
    />

    <SubstitutionModal
      isOpen={m.substitutionModal.isOpen}
      outgoingPlayer={m.substitutionModal.outgoingPlayer}
      benchPlayers={m.benchPlayersForSubstitution}
      onConfirm={m.handleSubstitutionConfirm}
      onCancel={() => m.setSubstitutionModal({ isOpen: false, outgoingPlayer: null })}
    />

    <CaptainTransferModal
      isOpen={m.captainTransferModal.isOpen}
      currentCaptain={m.captainTransferModal.currentCaptain}
      eligiblePlayers={m.eligiblePlayersForCaptain}
      onTransfer={m.handleCaptainTransfer}
      onCancel={() => m.setCaptainTransferModal({ isOpen: false, currentCaptain: null, pendingSubstitution: null })}
    />

    <VARReviewModal
      isOpen={m.varModal.isOpen}
      event={m.varModal.event}
      matchId={matchId}
      onClose={() => m.setVarModal({ isOpen: false, event: null })}
    />
    </>
  );
};
